import { imageBucket, avatarBucket } from '$lib/supabase';

// Cache for generated URLs
const urlCache = new Map<string, string>();

// Define standard sizes for different devices
const SIZES = {
	mobile: 300, // for thumbnail view
	tablet: 400, // for medium view
	desktop: 600 // for large view
};

function logImageError(url: string, error: any) {
	console.error('Image Error:', {
		url,
		error,
		timestamp: new Date().toISOString()
	});
}

export function getOptimizedImageUrl(path: string, width: number): string {
	try {
		const cacheKey = `${path}-${width}`;

		if (urlCache.has(cacheKey)) {
			return urlCache.get(cacheKey)!;
		}

		const {
			data: { publicUrl: baseUrl }
		} = imageBucket('images').getPublicUrl(path);

		const transformParams = new URLSearchParams({
			width: width.toString(),
			format: 'webp',
			quality: '80',
			resize: 'cover'
		});

		const finalUrl = `${baseUrl}?${transformParams}`;
		urlCache.set(cacheKey, finalUrl);

		return finalUrl;
	} catch (error) {
		logImageError(path, `URL generation error: ${error}`);
		return '';
	}
}

export function getOptimizedAvatarUrl(path: string, width: number): string {
	try {
		const cacheKey = `${path}-${width}`;

		if (urlCache.has(cacheKey)) {
			return urlCache.get(cacheKey)!;
		}

		const {
			data: { publicUrl: baseUrl }
		} = avatarBucket('avatars').getPublicUrl(path);

		const transformParams = new URLSearchParams({
			width: width.toString(),
			format: 'webp',
			quality: '80',
			resize: 'cover'
		});

		const finalUrl = `${baseUrl}?${transformParams}`;
		urlCache.set(cacheKey, finalUrl);

		return finalUrl;
	} catch (error) {
		logImageError(path, `URL generation error: ${error}`);
		return '';
	}
}

export function getOptimizedImageSet(path: string): { src: string; srcset: string } {
	try {
		const sizes = [SIZES.mobile, SIZES.tablet, SIZES.desktop];

		const srcset = sizes
			.map((size) => {
				const url = getOptimizedImageUrl(path, size);
				return `${url} ${size}w`;
			})
			.join(', ');

		// Use mobile size as default src
		const src = getOptimizedImageUrl(path, SIZES.mobile);

		return { src, srcset };
	} catch (error) {
		logImageError(path, `Image set generation error: ${error}`);
		return { src: '', srcset: '' };
	}
}

export function getBlurHash(path: string): string {
	try {
		const {
			data: { publicUrl: baseUrl }
		} = imageBucket('images').getPublicUrl(path);

		return `${baseUrl}?width=20&blur=10`;
	} catch (error) {
		logImageError(path, `Blur hash generation error: ${error}`);
		return '';
	}
}
